// Popover (audit §4b): a pick list that opens under its button (the board's Filter), on the pop layer:
// a frame, a 4 px cast, Close in the corner. React Aria places it (flipping above when there's no room
// below), moves focus into it and back to the button, and closes it on Escape or a click outside.
// Its content stacks with --pop-gap. Reads only --pop-*; the layer reads its own.
import type { ReactElement, ReactNode, RefObject } from 'react';
import { Dialog, DialogTrigger, Popover as AriaPopover } from 'react-aria-components';
import { LayerFrame } from '../Layer/Layer.tsx';
import { tokenPx } from '../token-value.ts';
import './Popover.css';

export interface PopoverProps {
  /** The button that opens it: a Button or an IconButton. */
  trigger: ReactElement;
  /** Its name for screen readers ("The filter"). */
  label: string;
  children: ReactNode;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  placement?: 'bottom start' | 'bottom end' | 'top start' | 'top end';
}

export function Popover({
  trigger,
  label,
  children,
  isOpen,
  onOpenChange,
  placement = 'bottom start',
}: PopoverProps) {
  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
      {trigger}
      <AriaPopover className="fl-pop" placement={placement} offset={tokenPx('--pop-offset')}>
        <Dialog className="fl-pop__dialog" aria-label={label}>
          {({ close }) => (
            <LayerFrame level="pop" label={label} onClose={close}>
              <div className="fl-pop__stack">{children}</div>
            </LayerFrame>
          )}
        </Dialog>
      </AriaPopover>
    </DialogTrigger>
  );
}

export interface PopLayerProps {
  /** What it sits under: a button, or a point a right-click marked (Menu draws that point). */
  triggerRef: RefObject<Element | null>;
  /** Its name for screen readers, and what its Close says ("Close job actions"). */
  label: string;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  children: ReactNode;
  placement?: PopoverProps['placement'];
  /** pop: --pop-w, a pick list; menu: --pop-w-menu, a list of actions. */
  width?: 'pop' | 'menu';
  /** Draws Close in the corner (true). A menu at the pointer has none: Escape or a click outside closes it. */
  showClose?: boolean;
  /** Its gap from what it sits under; --pop-offset unless it says (a menu at the pointer: 0). */
  offset?: number;
  /** Flips to the other side when there's more room there (true); off when the caller has placed it. */
  shouldFlip?: boolean;
  /** As tall as it may be (px); taller content scrolls inside. Unset: the room React Aria finds. */
  maxHeight?: number;
  /** Kept clear at the screen's edges (px); React Aria's 12 unless it says. */
  edge?: number;
}

/**
 * The pop layer on its own, for content that isn't a dialog (Menu's list of actions): React Aria places
 * it and closes it on Escape or a click outside; the layer draws the frame, the cast and Close. The
 * children are the layer's body as they are, so the first of them sits beside Close.
 */
export function PopLayer({
  triggerRef,
  label,
  isOpen,
  onOpenChange,
  children,
  placement = 'bottom start',
  width = 'pop',
  showClose = true,
  offset,
  shouldFlip,
  maxHeight,
  edge,
}: PopLayerProps) {
  return (
    <AriaPopover
      className="fl-pop"
      data-width={width}
      aria-label={label}
      triggerRef={triggerRef}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      placement={placement}
      offset={offset ?? tokenPx('--pop-offset')}
      {...(shouldFlip !== undefined ? { shouldFlip } : {})}
      {...(maxHeight !== undefined ? { maxHeight } : {})}
      {...(edge !== undefined ? { containerPadding: edge } : {})}
    >
      <LayerFrame
        level="pop"
        label={label}
        onClose={showClose ? () => onOpenChange(false) : undefined}
        // a menu keeps to the room it's given and scrolls inside past it
        fit={width === 'menu'}
      >
        {children}
      </LayerFrame>
    </AriaPopover>
  );
}
